'use client';

import { useMemo, useState } from 'react';
import { BarChart3, Loader2 } from 'lucide-react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  XAxis,
  YAxis,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart';

/**
 * Matte categorical palette — distinct hues at similar desaturation and
 * lightness, so endpoints read as separate categories without any one bar
 * screaming for attention.
 */
const CHART_COLORS = [
  '#6b8ea3', // slate blue
  '#b07a8a', // muted rose
  '#8fa876', // sage
  '#c9a86a', // muted mustard
  '#9989b8', // muted lavender
  '#c08560', // terracotta
  '#5b9a92', // muted teal
  '#a8896f', // taupe
];

/** The credits line, deliberately outside the categorical palette. */
const CREDIT_COLOR = '#c2410c';

export interface UsageDay {
  date: string; // ISO "YYYY-MM-DD"
  count: number;
  credit: number;
  by_endpoint: Record<string, number>;
  by_credit: Record<string, number>;
}

const CREDITS_KEY = '__credits__';

function formatShortDate(iso: string): string {
  const d = new Date(iso);
  return `${d.getDate()}.${d.getMonth() + 1}`;
}

function formatLongDate(iso: string): string {
  return new Date(iso).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

const fmt = (v: number) => v.toLocaleString('de-DE');

/**
 * Requests and credits on one chart, plus what the credits went on.
 *
 * They were two side-by-side charts with identical shapes and different
 * scales, which made the only question worth asking — "which calls are
 * costing me?" — a matter of eyeballing two pictures. Bars carry requests
 * per day (stacked by endpoint, left axis) and a line carries credits spent
 * (right axis), so a cheap spike and an expensive one look different.
 *
 * The breakdown below is the same data per endpoint over the whole window,
 * ordered by cost: the answer, in numbers, under the shape.
 *
 * Shared by /account/usage and /admin/api — the two had drifted copies of
 * the same component.
 */
export function UsageChart({
  title = 'Usage',
  days,
  daily,
  byEndpointTotal,
  byCreditTotal,
  total,
  totalCredits,
  isLoading,
  footnote,
}: {
  title?: string;
  days: number;
  daily: UsageDay[];
  byEndpointTotal: Record<string, number>;
  byCreditTotal: Record<string, number>;
  total: number;
  totalCredits: number;
  isLoading: boolean;
  /** e.g. the v1 / MCP split, which only some callers have. */
  footnote?: React.ReactNode;
}) {
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  // One stable key set across all days so every bar stacks the same way;
  // days missing a key contribute 0 rather than a gap.
  const { keys, rows } = useMemo(() => {
    const keySet = new Set<string>();
    for (const day of daily) {
      for (const k of Object.keys(day.by_endpoint ?? {})) keySet.add(k);
    }
    const keyList = Array.from(keySet).sort();
    const rowList = daily.map((day) => {
      const row: Record<string, number | string> = { date: day.date };
      for (const k of keyList) row[k] = day.by_endpoint?.[k] ?? 0;
      // Filtering to one endpoint must move the line too, or the chart shows
      // one endpoint's requests against everyone's credits.
      row[CREDITS_KEY] = selectedKey
        ? (day.by_credit?.[selectedKey] ?? 0)
        : day.credit;
      return row;
    });
    return { keys: keyList, rows: rowList };
  }, [daily, selectedKey]);

  const chartConfig = useMemo<ChartConfig>(() => {
    const cfg: ChartConfig = {
      [CREDITS_KEY]: { label: 'Credits', color: CREDIT_COLOR },
    };
    keys.forEach((k, i) => {
      cfg[k] = { label: k, color: CHART_COLORS[i % CHART_COLORS.length] };
    });
    return cfg;
  }, [keys]);

  const visibleKeys = selectedKey ? [selectedKey] : keys;
  const shownRequests = selectedKey
    ? (byEndpointTotal[selectedKey] ?? 0)
    : total;
  const shownCredits = selectedKey
    ? (byCreditTotal[selectedKey] ?? 0)
    : totalCredits;

  // The breakdown: every endpoint seen in the window, dearest first. Credits
  // are the point, so an endpoint that cost nothing sorts below one that did
  // even if it was called more often.
  const breakdown = useMemo(() => {
    const all = new Set([
      ...Object.keys(byEndpointTotal ?? {}),
      ...Object.keys(byCreditTotal ?? {}),
    ]);
    return Array.from(all)
      .map((endpoint) => ({
        endpoint,
        requests: byEndpointTotal?.[endpoint] ?? 0,
        credits: byCreditTotal?.[endpoint] ?? 0,
      }))
      .sort((a, b) => b.credits - a.credits || b.requests - a.requests);
  }, [byEndpointTotal, byCreditTotal]);

  return (
    <Card variant="grid">
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center justify-between gap-2 w-full">
          <span>{title}</span>
          <span className="text-sm font-normal text-muted-foreground">
            <span className="text-2xl font-semibold tabular-nums text-foreground">
              {fmt(shownRequests)}
            </span>{' '}
            requests ·{' '}
            <span
              className="text-2xl font-semibold tabular-nums"
              style={{ color: CREDIT_COLOR }}
            >
              {fmt(shownCredits)}
            </span>{' '}
            credits / {days}d
          </span>
        </CardTitle>
        {footnote && (
          <div className="mt-0.5 flex justify-end text-xs tabular-nums text-muted-foreground">
            {footnote}
          </div>
        )}
      </CardHeader>
      <CardContent className="px-5 pt-0 pb-6">
        {isLoading ? (
          <div className="flex h-[260px] items-center justify-center text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Loading...
          </div>
        ) : total === 0 && totalCredits === 0 ? (
          <div className="flex h-[260px] flex-col items-center justify-center gap-1 text-sm text-muted-foreground">
            <BarChart3 className="h-8 w-8 opacity-40" />
            <span>No activity in this range</span>
          </div>
        ) : (
          <>
            <ChartContainer config={chartConfig} className="h-[260px] w-full">
              <ComposedChart data={rows} barGap={0} barCategoryGap="25%">
                <CartesianGrid vertical={false} strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  tickFormatter={formatShortDate}
                  interval="preserveStartEnd"
                  fontSize={11}
                />
                <YAxis
                  yAxisId="requests"
                  tickLine={false}
                  axisLine={false}
                  width={40}
                  fontSize={11}
                  allowDecimals={false}
                />
                <YAxis
                  yAxisId="credits"
                  orientation="right"
                  tickLine={false}
                  axisLine={false}
                  width={44}
                  fontSize={11}
                  allowDecimals={false}
                  stroke={CREDIT_COLOR}
                />
                <ChartTooltip
                  content={
                    <ChartTooltipContent
                      labelFormatter={(_, payload) => {
                        const d = payload?.[0]?.payload?.date;
                        return d ? formatLongDate(d) : '';
                      }}
                    />
                  }
                />
                {visibleKeys.map((k, i) => (
                  <Bar
                    key={k}
                    yAxisId="requests"
                    dataKey={k}
                    stackId="usage"
                    fill={CHART_COLORS[keys.indexOf(k) % CHART_COLORS.length]}
                    stroke="var(--background)"
                    strokeWidth={1}
                    radius={
                      i === visibleKeys.length - 1 ? [2, 2, 0, 0] : [0, 0, 0, 0]
                    }
                  />
                ))}
                <Line
                  yAxisId="credits"
                  type="monotone"
                  dataKey={CREDITS_KEY}
                  stroke={CREDIT_COLOR}
                  strokeWidth={2}
                  dot={false}
                />
              </ComposedChart>
            </ChartContainer>

            <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1">
              {keys.map((k, i) => {
                const isActive = selectedKey === k;
                const dim = selectedKey !== null && !isActive;
                return (
                  <button
                    type="button"
                    key={k}
                    onClick={() => setSelectedKey(isActive ? null : k)}
                    title={isActive ? 'Clear filter' : `Filter to ${k}`}
                    className={`flex cursor-pointer items-center gap-1.5 text-xs transition-opacity hover:opacity-100 ${
                      dim
                        ? 'opacity-40 text-muted-foreground'
                        : isActive
                          ? 'font-medium text-foreground'
                          : 'text-muted-foreground'
                    }`}
                  >
                    <span
                      className="inline-block h-2.5 w-2.5 shrink-0 rounded-sm"
                      style={{
                        background: CHART_COLORS[i % CHART_COLORS.length],
                      }}
                    />
                    <span className="truncate font-mono">{k}</span>
                  </button>
                );
              })}
            </div>

            {breakdown.length > 0 && (
              <div className="mt-5 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-xs text-muted-foreground">
                      <th className="pb-2 font-medium">Endpoint</th>
                      <th className="pb-2 text-right font-medium">Requests</th>
                      <th className="pb-2 text-right font-medium">Credits</th>
                      <th className="pb-2 text-right font-medium">
                        Ø per request
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {breakdown.map((row) => (
                      <tr
                        key={row.endpoint}
                        className={
                          selectedKey && selectedKey !== row.endpoint
                            ? 'opacity-40'
                            : undefined
                        }
                      >
                        <td className="py-2 pr-3 font-mono text-xs">
                          {row.endpoint}
                        </td>
                        <td className="py-2 pr-3 text-right tabular-nums">
                          {fmt(row.requests)}
                        </td>
                        <td
                          className="py-2 pr-3 text-right tabular-nums font-medium"
                          style={
                            row.credits > 0 ? { color: CREDIT_COLOR } : undefined
                          }
                        >
                          {fmt(row.credits)}
                        </td>
                        <td className="py-2 text-right tabular-nums text-muted-foreground">
                          {row.requests > 0
                            ? (row.credits / row.requests).toLocaleString(
                                'de-DE',
                                { maximumFractionDigits: 1 },
                              )
                            : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
